// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  getOcspStatus, getOcspCompliance, getOcspLogs, getMtlsInstitutions,
  revokeMtlsCert, reinstateMtlsCert, setMtlsEnforcement, clearOcspCache,
  regenerateCrl, verifyCertificate, triggerExpiryCheck, getEmailLog,
  getNotificationSettings, updateNotificationSettings,
} from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, ShieldX, Lock, Unlock, RefreshCw,
  AlertTriangle, CheckCircle2, XCircle, Clock, FileKey, Server,
  Activity, Download, Trash2, Eye, Search, Bell, Mail, Settings2,
} from "lucide-react";

const StatusDot = ({ status }) => {
  const colors = {
    good: "bg-emerald-500", active: "bg-emerald-500",
    revoked: "bg-red-500", expired: "bg-amber-500",
    unknown: "bg-gray-500",
  };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[status] || colors.unknown}`} />;
};

const ComplianceBadge = ({ compliant, label }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
    compliant ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"
  }`}>
    {compliant ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-red-400" />}
    <span className={`text-sm font-medium ${compliant ? "text-emerald-400" : "text-red-400"}`}>{label}</span>
  </div>
);

export default function Certificates() {
  const [ocspStatus, setOcspStatus] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [ocspLogs, setOcspLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokeDialog, setRevokeDialog] = useState({ open: false, institution: null });
  const [revokeReason, setRevokeReason] = useState("administrative");
  const [verifyDialog, setVerifyDialog] = useState({ open: false, data: null });
  const [searchSerial, setSearchSerial] = useState("");
  const [emailLog, setEmailLog] = useState([]);
  const [notifSettings, setNotifSettings] = useState(null);
  const [expiryCheckResult, setExpiryCheckResult] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [statusRes, compRes, instRes, logsRes, emailRes, settingsRes] = await Promise.all([
        getOcspStatus(), getOcspCompliance(), getMtlsInstitutions(), getOcspLogs(50),
        getEmailLog(20), getNotificationSettings(),
      ]);
      setOcspStatus(statusRes.data);
      setCompliance(compRes.data);
      setInstitutions(instRes.data || []);
      setOcspLogs(logsRes.data || []);
      setEmailLog(emailRes.data || []);
      setNotifSettings(settingsRes.data || null);
    } catch (e) {
      toast.error("Failed to load certificate data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRevoke = async () => {
    const inst = revokeDialog.institution;
    if (!inst) return;
    try {
      await revokeMtlsCert(inst.dn, revokeReason);
      toast.success(`Certificate revoked: ${inst.institution_name}`);
      setRevokeDialog({ open: false, institution: null });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke certificate");
    }
  };

  const handleReinstate = async (inst) => {
    try {
      await reinstateMtlsCert(inst.dn);
      toast.success(`Certificate reinstated: ${inst.institution_name}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reinstate certificate");
    }
  };

  const handleEnforcementChange = async (mode) => {
    try {
      await setMtlsEnforcement(mode);
      toast.success(`Enforcement mode set to: ${mode}`);
      fetchAll();
    } catch (e) {
      toast.error("Failed to change enforcement mode");
    }
  };

  const handleClearCache = async () => {
    try {
      await clearOcspCache();
      toast.success("OCSP cache cleared");
    } catch (e) {
      toast.error("Failed to clear cache");
    }
  };

  const handleRegenerateCrl = async () => {
    try {
      await regenerateCrl();
      toast.success("CRL regenerated");
      fetchAll();
    } catch (e) {
      toast.error("Failed to regenerate CRL");
    }
  };

  const handleVerify = async (serial) => {
    try {
      const res = await verifyCertificate(serial);
      setVerifyDialog({ open: true, data: res.data });
    } catch (e) {
      toast.error("Verification failed");
    }
  };

  const handleSearchVerify = async () => {
    if (!searchSerial.trim()) return;
    await handleVerify(searchSerial.trim());
  };

  const handleExpiryCheck = async () => {
    try {
      const res = await triggerExpiryCheck();
      setExpiryCheckResult(res.data);
      if (res.data.alerts_triggered > 0) {
        toast.warning(`${res.data.alerts_triggered} expiry alert(s) triggered`);
      } else {
        toast.success(`Checked ${res.data.checked} certificates — no expiry alerts`);
      }
      fetchAll();
    } catch (e) {
      toast.error("Expiry check failed");
    }
  };

  const handleToggleSetting = async (key) => {
    if (!notifSettings) return;
    const updated = { ...notifSettings, [key]: !notifSettings[key] };
    try {
      await updateNotificationSettings(updated);
      setNotifSettings(updated);
      toast.success("Settings updated");
    } catch (e) {
      toast.error("Failed to update settings");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="certificates-loading">
        <div className="animate-pulse text-muted-foreground">Loading certificate data...</div>
      </div>
    );
  }

  const s = ocspStatus || {};
  const c = compliance || {};

  return (
    <div className="space-y-6" data-testid="certificates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Certificate Management</h1>
          <p className="text-muted-foreground">OCSP Responder, mTLS Certificates & Compliance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleClearCache} data-testid="clear-cache-btn">
            <Trash2 className="h-4 w-4 mr-1" /> Clear Cache
          </Button>
          <Button variant="outline" size="sm" onClick={handleRegenerateCrl} data-testid="regen-crl-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Regenerate CRL
          </Button>
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* OCSP Responder Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border" data-testid="ocsp-status-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-full ${s.responder_operational ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
                <Server className={`h-5 w-5 ${s.responder_operational ? "text-emerald-400" : "text-red-400"}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">OCSP Responder</p>
                <p className={`text-lg font-bold ${s.responder_operational ? "text-emerald-400" : "text-red-400"}`}>
                  {s.responder_operational ? "Operational" : "Offline"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="certs-active-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-full bg-blue-500/10">
                <FileKey className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Active Certificates</p>
                <p className="text-lg font-bold text-blue-400">{s.active_certificates || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="certs-revoked-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-full bg-red-500/10">
                <ShieldX className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Revoked</p>
                <p className="text-lg font-bold text-red-400">{s.revoked_certificates || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="ocsp-requests-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-full bg-cyan-500/10">
                <Activity className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">OCSP Requests (24h)</p>
                <p className="text-lg font-bold text-cyan-400">{s.ocsp_requests_24h || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Dashboard + CA Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Compliance */}
        <Card className="bg-card border-border" data-testid="compliance-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Regulatory Compliance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 mb-3">
              <Badge variant={c.overall_compliant ? "default" : "destructive"} className="text-sm">
                {c.overall_compliant ? "Fully Compliant" : "Action Required"}
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ComplianceBadge compliant={c.sarb_nps?.compliant} label="SARB NPS" />
              <ComplianceBadge compliant={c.pci_dss_section4?.compliant} label="PCI-DSS S4" />
              <ComplianceBadge compliant={c.ifwg_sandbox?.compliant} label="IFWG Sandbox" />
            </div>

            {/* Detailed checks */}
            <div className="space-y-2 pt-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">PCI-DSS Section 4 Checks</p>
              {c.pci_dss_section4 && Object.entries(c.pci_dss_section4).filter(([k]) => k !== "compliant").map(([key, val]) => (
                <div key={key} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
                  {val ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-red-400" />}
                </div>
              ))}
            </div>

            {c.recommendations?.length > 0 && (
              <div className="mt-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                <p className="text-xs font-semibold text-amber-400 mb-1">Recommendations</p>
                {c.recommendations.map((r, i) => (
                  <p key={i} className="text-xs text-amber-300 flex items-start gap-1">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {r}
                  </p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CA Info + Enforcement */}
        <Card className="bg-card border-border" data-testid="ca-info-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Certificate Authority
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {s.ca_info ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subject</span>
                  <span className="font-mono text-xs">{s.ca_info.subject}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valid Until</span>
                  <span>{new Date(s.ca_info.not_valid_after).toLocaleDateString("en-ZA")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Days Until Expiry</span>
                  <span className={s.ca_info.days_until_expiry < 90 ? "text-amber-400" : "text-emerald-400"}>
                    {s.ca_info.days_until_expiry}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fingerprint</span>
                  <span className="font-mono text-[10px] text-muted-foreground truncate max-w-[200px]">{s.ca_info.fingerprint_sha256}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">CA not initialized</p>
            )}

            <div className="pt-3 border-t border-border space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">mTLS Enforcement</p>
              <div className="flex items-center gap-3">
                <Select value={s.enforcement_mode || "log_only"} onValueChange={handleEnforcementChange}>
                  <SelectTrigger className="w-48" data-testid="enforcement-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enforce">Enforce (Block)</SelectItem>
                    <SelectItem value="log_only">Log Only (Monitor)</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
                <Badge variant={
                  s.enforcement_mode === "enforce" ? "default" :
                  s.enforcement_mode === "log_only" ? "secondary" : "outline"
                }>
                  {s.enforcement_mode === "enforce" ? "Production" :
                   s.enforcement_mode === "log_only" ? "Sandbox" : "Off"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {s.enforcement_mode === "enforce" ? "Requests without valid client certificates are rejected" :
                 s.enforcement_mode === "log_only" ? "Invalid certificates are logged but requests proceed" :
                 "mTLS checking is disabled"}
              </p>
            </div>

            <div className="pt-3 border-t border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">OCSP Stats</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-secondary/30 rounded-lg p-2">
                  <p className="text-lg font-bold">{s.cached_responses || 0}</p>
                  <p className="text-[10px] text-muted-foreground">Cached</p>
                </div>
                <div className="bg-secondary/30 rounded-lg p-2">
                  <p className="text-lg font-bold">{s.crl_available ? "Yes" : "No"}</p>
                  <p className="text-[10px] text-muted-foreground">CRL Ready</p>
                </div>
                <div className="bg-secondary/30 rounded-lg p-2">
                  <p className="text-lg font-bold">{s.response_validity_hours || 24}h</p>
                  <p className="text-[10px] text-muted-foreground">Cache TTL</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Certificate Verification Search */}
      <Card className="bg-card border-border" data-testid="cert-verify-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            OCSP Certificate Verification
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Enter certificate serial number (hex)"
              value={searchSerial}
              onChange={(e) => setSearchSerial(e.target.value)}
              className="max-w-md font-mono"
              data-testid="cert-serial-input"
            />
            <Button onClick={handleSearchVerify} data-testid="verify-cert-btn">
              <Eye className="h-4 w-4 mr-1" /> Verify
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Query the OCSP responder for a certificate's revocation status</p>
        </CardContent>
      </Card>

      {/* Certificate Registry Table */}
      <Card className="bg-card border-border" data-testid="cert-registry-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileKey className="h-5 w-5 text-primary" />
            Certificate Registry ({institutions.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Institution</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Serial</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Days Left</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {institutions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No certificates registered
                  </TableCell>
                </TableRow>
              ) : (
                institutions.map((inst) => (
                  <TableRow key={inst.dn}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <StatusDot status={inst.status} />
                        <span className="font-medium">{inst.institution_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{inst.role}</Badge>
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-xs bg-secondary px-1.5 py-0.5 rounded">{inst.serial}</code>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={inst.status === "active" ? "default" : "destructive"}
                        className="text-xs"
                        data-testid={`cert-status-${inst.institution_name}`}
                      >
                        {inst.status === "active" ? "Active" : "Revoked"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {inst.expires_at ? new Date(inst.expires_at).toLocaleDateString("en-ZA") : "—"}
                    </TableCell>
                    <TableCell>
                      {inst.days_until_expiry !== null && inst.days_until_expiry !== undefined ? (
                        <span className={`text-sm font-mono ${
                          inst.days_until_expiry < 0 ? "text-red-400" :
                          inst.days_until_expiry < 30 ? "text-amber-400" : "text-emerald-400"
                        }`}>
                          {inst.days_until_expiry < 0 ? `Expired ${Math.abs(inst.days_until_expiry)}d ago` : `${inst.days_until_expiry}d`}
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleVerify(inst.serial)}
                          data-testid={`verify-${inst.institution_name}`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {inst.status === "active" ? (
                          <Button
                            variant="ghost" size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => setRevokeDialog({ open: true, institution: inst })}
                            data-testid={`revoke-${inst.institution_name}`}
                          >
                            <Lock className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost" size="sm"
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            onClick={() => handleReinstate(inst)}
                            data-testid={`reinstate-${inst.institution_name}`}
                          >
                            <Unlock className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Expiring Soon Alerts */}
      {s.expiring_within_30_days?.length > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/20" data-testid="expiring-certs-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-400">
              <AlertTriangle className="h-5 w-5" />
              Certificates Expiring Within 30 Days
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {s.expiring_within_30_days.map((cert, i) => (
                <div key={i} className="flex items-center justify-between text-sm bg-amber-500/5 px-3 py-2 rounded-lg">
                  <span className="font-medium">{cert.institution}</span>
                  <span className="text-amber-300 font-mono">{cert.days_left} days remaining</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notification Settings + Expiry Check */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-card border-border" data-testid="notif-settings-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-primary" />
              Notification Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {notifSettings && (
              <>
                <div className="space-y-3">
                  {[
                    { key: "cert_expiry_enabled", label: "Certificate Expiry Alerts", desc: `Alert ${notifSettings.expiry_threshold_days || 30} days before expiry` },
                    { key: "revocation_alerts_enabled", label: "Revocation Alerts", desc: "Alert when certificates are revoked" },
                    { key: "downtime_alerts_enabled", label: "OCSP Downtime Alerts", desc: "Alert when OCSP responder is unavailable" },
                    { key: "compliance_alerts_enabled", label: "Compliance Change Alerts", desc: "Alert when compliance status changes" },
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{label}</p>
                        <p className="text-xs text-muted-foreground">{desc}</p>
                      </div>
                      <Switch
                        checked={notifSettings[key] ?? true}
                        onCheckedChange={() => handleToggleSetting(key)}
                        data-testid={`toggle-${key}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="pt-3 border-t border-border">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Email provider:</span>
                    <Badge variant="outline" className="text-xs">
                      {notifSettings.email_provider === "mock" ? "Mock (DB only)" : notifSettings.email_provider}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Emails are logged to database. Connect SendGrid/Resend for live delivery.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="expiry-check-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              Certificate Expiry Check
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Scan all active certificates and trigger alerts for any expiring within 30 days.
            </p>
            <Button onClick={handleExpiryCheck} data-testid="run-expiry-check-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Run Expiry Check
            </Button>
            {expiryCheckResult && (
              <div className="mt-3 p-3 bg-secondary/20 rounded-lg text-sm space-y-1">
                <p>Certificates checked: <strong>{expiryCheckResult.checked}</strong></p>
                <p>Alerts triggered: <strong className={expiryCheckResult.alerts_triggered > 0 ? "text-amber-400" : "text-emerald-400"}>
                  {expiryCheckResult.alerts_triggered}
                </strong></p>
                {expiryCheckResult.alerts?.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-amber-400 text-xs mt-1">
                    <AlertTriangle className="h-3 w-3" />
                    {a.institution} — {a.days_left} days left
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Log */}
      <Card className="bg-card border-border" data-testid="email-log-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Email Delivery Log
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {emailLog.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                    No emails sent yet — run an expiry check or revoke a certificate to trigger
                  </TableCell>
                </TableRow>
              ) : (
                emailLog.slice(0, 15).map((em, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm">{new Date(em.created_at).toLocaleString("en-ZA")}</TableCell>
                    <TableCell className="text-sm">{em.to}</TableCell>
                    <TableCell className="text-sm max-w-[300px] truncate">{em.subject}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{em.provider}</Badge></TableCell>
                    <TableCell>
                      <Badge variant={em.status === "mock_sent" ? "secondary" : "default"} className="text-xs">
                        {em.status === "mock_sent" ? "Mock" : em.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* OCSP Request Logs */}
      <Card className="bg-card border-border" data-testid="ocsp-logs-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            OCSP Request Log
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Source IP</TableHead>
                <TableHead>Request Size</TableHead>
                <TableHead>Response Size</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ocspLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                    No OCSP requests recorded yet
                  </TableCell>
                </TableRow>
              ) : (
                ocspLogs.slice(0, 20).map((log, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm">
                      {new Date(log.timestamp).toLocaleString("en-ZA")}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{log.source_ip}</TableCell>
                    <TableCell className="text-sm">{log.request_size} bytes</TableCell>
                    <TableCell className="text-sm">{log.response_size} bytes</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Revoke Dialog */}
      <Dialog open={revokeDialog.open} onOpenChange={(open) => !open && setRevokeDialog({ open: false, institution: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <ShieldAlert className="h-5 w-5" />
              Revoke Certificate
            </DialogTitle>
            <DialogDescription>
              This will immediately revoke the certificate for <strong>{revokeDialog.institution?.institution_name}</strong>.
              The institution will be unable to authenticate via mTLS until reinstated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Revocation Reason</Label>
              <Select value={revokeReason} onValueChange={setRevokeReason}>
                <SelectTrigger data-testid="revoke-reason-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="administrative">Administrative</SelectItem>
                  <SelectItem value="key_compromise">Key Compromise</SelectItem>
                  <SelectItem value="ca_compromise">CA Compromise</SelectItem>
                  <SelectItem value="affiliation_changed">Affiliation Changed</SelectItem>
                  <SelectItem value="superseded">Superseded</SelectItem>
                  <SelectItem value="cessation_of_operation">Cessation of Operation</SelectItem>
                  <SelectItem value="privilege_withdrawn">Privilege Withdrawn</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-red-500/5 border border-red-500/20 p-3 rounded-lg text-sm">
              <p className="text-red-400 font-medium">Warning</p>
              <p className="text-muted-foreground mt-1">
                Per SARB NPS and PCI-DSS requirements, this action will be permanently logged in the audit trail.
                The OCSP responder will immediately report this certificate as revoked.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setRevokeDialog({ open: false, institution: null })}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleRevoke} data-testid="confirm-revoke-btn">
                <Lock className="h-4 w-4 mr-1" /> Revoke Certificate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Verify Dialog */}
      <Dialog open={verifyDialog.open} onOpenChange={(open) => !open && setVerifyDialog({ open: false, data: null })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              OCSP Verification Result
            </DialogTitle>
          </DialogHeader>
          {verifyDialog.data && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Badge
                  variant={verifyDialog.data.ocsp_status === "good" ? "default" : "destructive"}
                  className="text-sm"
                  data-testid="verify-result-status"
                >
                  {verifyDialog.data.ocsp_status === "good" ? "GOOD" :
                   verifyDialog.data.ocsp_status === "revoked" ? "REVOKED" : "UNKNOWN"}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Serial: <code className="font-mono">{verifyDialog.data.serial}</code>
                </span>
              </div>

              {verifyDialog.data.institution && (
                <div className="space-y-2 text-sm bg-secondary/20 rounded-lg p-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Institution</span>
                    <span className="font-medium">{verifyDialog.data.institution}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">DN</span>
                    <span className="font-mono text-xs">{verifyDialog.data.dn}</span>
                  </div>
                  {verifyDialog.data.expires_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Expires</span>
                      <span>{new Date(verifyDialog.data.expires_at).toLocaleDateString("en-ZA")}</span>
                    </div>
                  )}
                  {verifyDialog.data.revoked_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Revoked At</span>
                      <span className="text-red-400">{new Date(verifyDialog.data.revoked_at).toLocaleString("en-ZA")}</span>
                    </div>
                  )}
                  {verifyDialog.data.revoke_reason && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Reason</span>
                      <span className="text-red-400">{verifyDialog.data.revoke_reason}</span>
                    </div>
                  )}
                </div>
              )}

              {verifyDialog.data.cert_details && (
                <div className="space-y-2 text-sm bg-secondary/20 rounded-lg p-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Certificate Details</p>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Algorithm</span>
                    <span className="font-mono text-xs">{verifyDialog.data.cert_details.signature_algorithm}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">SHA-256 Fingerprint</span>
                    <span className="font-mono text-[10px] truncate max-w-[250px]">{verifyDialog.data.cert_details.fingerprint_sha256}</span>
                  </div>
                </div>
              )}

              {!verifyDialog.data.institution && (
                <p className="text-sm text-muted-foreground">{verifyDialog.data.message || "Certificate not found in registry"}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
